/**
 * BisForm class represents a custom web component that extends HTMLElement.
 * It provides functionality to handle form submission for back-in-stock notifications.
 */
class BisForm extends window.HTMLElement {
  constructor() {
    super()
    // Get relevant data attributes from the dataset.
    this.listId = this.dataset.listId
    this.apiKey = this.dataset.apiKey
    this.prodId = this.dataset.productId
  }

  /**
   * Callback triggered when the component is connected to the DOM.
   * Sets up event listeners and references to form elements.
   */
  connectedCallback() {
    this.form = this.querySelector('form')
    this.successWrapper = this.querySelector('[bis-success]')
    this.messageWrapper = this.querySelector('[bis-message]')
    this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
  }

  /**
   * Callback triggered when the component is disconnected from the DOM.
   * Removes the form submit event listener.
   */
  disconnectedCallback() {
    this.form.removeEventListener('submit', this.onSubmitHandler.bind(this))
  }

  /**
   * Handles form submission for back-in-stock notifications.
   * @param {Event} evt - The form submit event.
   */
  onSubmitHandler(evt) {
    evt.preventDefault()

    // Identify the product form related to the clicked submit button.
    const productForm = evt.submitter
      .closest('section')
      .querySelector('product-form form')
    evt.submitter.classList.add('loading')

    // Get variant input from the product form.
    const variantInput = productForm.querySelector('[name="id"]')

    // Create a FormData object from the form.
    const formData = new window.FormData(this.form)

    // Extract form data values.
    const email = formData.get('email')
    const phoneNumber = formData.get('phone_number')
    const subscribeForNewsletter = formData.get('subscribeForNewsletter')
    const subscribeForNewsletterBool = subscribeForNewsletter === 'on'

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Klaviyo-API-Key ${this.apiKey}`,
      revision: '2024-06-15',
    }

    // Prepare data for sending.
    const bisPayload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
                ...(phoneNumber && { phone_number: `${phoneNumber}` }),
              },
            },
          },
          channels: ['EMAIL', ...(phoneNumber ? ['SMS'] : [])],
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantInput.value}`,
            },
          },
        },
      },
    }

    const createEmailSmsSubscription = () => {
      return fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${this.apiKey}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      ...(phoneNumber && { phone_number: `${phoneNumber}` }),
                    },
                  },
                },
                custom_source: 'Back in Stock',
              },
              relationships: {
                list: {
                  data: {
                    type: 'list',
                    id: this.listId,
                  },
                },
              },
            },
          }),
        }
      ).then((res) => {
        if (!res.ok) throw new Error(`Email API Error (${res.status})`)
        const contentType = res.headers.get('Content-Type')
        if (contentType && contentType.includes('application/json')) {
          return res.json()
        } else {
          return null
        }
      })
    }

    // Send a POST request to the back-in-stock notification API.
    fetch(
      `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${this.apiKey}`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(bisPayload),
      }
    )
      .then(async (response) => {
        if (!response.ok) {
          let errorMessage = 'BIS request failed'
          try {
            const errorJson = await response.json()
            errorMessage = errorJson?.errors?.[0]?.detail || errorMessage
          } catch (parseErr) {
            console.warn('Failed to parse error JSON:', parseErr)
          }
          throw new Error(errorMessage)
        }

        // ✅ Email list subscription
        if (subscribeForNewsletterBool && email !== '') {
          createEmailSmsSubscription()
            .then((data) => console.log('✅ Subscribed:', data))
            .catch((err) => console.error('❌ Error:', err))
        }

        // Reset the form, show success message, and update button state.
        this.form.reset()
        this.form.classList.add('hidden')
        this.messageWrapper.classList.add('hidden')
        this.successWrapper.classList.remove('hidden')
        evt.submitter.classList.remove('loading')

        // ✅ Only try to parse JSON if there's a body
        const contentType = response.headers.get('Content-Type')
        if (contentType && contentType.includes('application/json')) {
          return response.json()
        } else {
          return null
        }
      })
      .then((bisData) => {
        console.log('✅ BIS submitted:', bisData)
      })
      .catch((error) => {
        // Reset the form, show error message, and update button state
        this.form.reset()
        evt.submitter.classList.remove('loading')
        this.messageWrapper.classList.remove('hidden')

        // Determine error source and provide specific message
        let errorMessage = 'An unexpected error occurred'
        const msg = error.message.toLowerCase()

        if (msg.includes('bis') || msg.includes('back-in-stock')) {
          errorMessage = `Back-in-Stock Error: ${error.message}`
        } else if (msg.includes('sms')) {
          errorMessage = `SMS Error: ${error.message}`
        } else if (msg.includes('email')) {
          errorMessage = `Email Subscription Error: ${error.message}`
        } else if (error instanceof TypeError) {
          errorMessage = 'Network Error: Please check your internet connection'
        }

        this.messageWrapper.textContent = errorMessage
      })
  }
}

// Define the 'bis-form' custom element using the BisForm class.
window.customElements.define('bis-form', BisForm)
